import clsx from 'clsx';
import React, { useEffect } from 'react';

type ModalProps = {
  isOpen: boolean;
  onClose: () => void;
  children: React.ReactNode;
  width?: number;
  minHeight?: number;
  header?: React.ReactNode;
};

const widthClasses: Partial<Record<number, string>> = {
  400: 'w-[400px]',
  500: 'w-[500px]',
  750: 'w-[750px]',
  760: 'w-[760px]',
  800: 'w-[800px]',
};

const minHeightClasses: Partial<Record<number, string>> = {
  400: 'min-h-[400px]',
  500: 'min-h-[500px]',
  580: 'min-h-[580px]',
  600: 'min-h-[600px]',
  640: 'min-h-[640px]',
};

export const Modal: React.FC<ModalProps> = ({ isOpen, onClose, children, width = 760, minHeight, header }) => {
  // Disable body scroll when modal is open using CSS class
  useEffect(() => {
    if (isOpen) {
      document.body.classList.add('modal-open');
    } else {
      document.body.classList.remove('modal-open');
    }

    // Cleanup: remove class when component unmounts
    return () => {
      document.body.classList.remove('modal-open');
    };
  }, [isOpen]);

  if (!isOpen) return null;

  return (
    <div className="fixed top-0 left-0 w-full h-full bg-black/50 backdrop-blur-sm flex justify-center items-center z-[1000]">
      <div
        className={clsx(
          'bg-white rounded-3xl relative shadow-xl max-h-[90vh] flex flex-col overflow-hidden',
          widthClasses[width],
          minHeight && minHeightClasses[minHeight]
        )}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header with close button - always show close button */}
        <div className="w-full flex justify-between items-center px-6 pt-6 pb-0 flex-shrink-0">
          {header ? <div className="flex-1">{header}</div> : <div className="flex-1" />}
          <button
            onClick={onClose}
            className="w-8 h-8 flex justify-center items-center bg-transparent border-none cursor-pointer hover:bg-gray-100 rounded-full transition-colors flex-shrink-0"
            aria-label="Close modal"
          >
            <img src="/material-symbols-close.svg" alt="Close" className="w-6 h-6" />
          </button>
        </div>
        <div className="flex-1 overflow-hidden flex flex-col min-h-0">{children}</div>
      </div>
    </div>
  );
};
